import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Work as WorkIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import apiService from '../services/api';

const JobsPage = () => {
  const navigate = useNavigate();
  const { getAuthToken } = useAuth();
  const { config } = useConfig();
  
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');

  useEffect(() => {
    if (config && getAuthToken) {
      apiService.initialize(config.apiGatewayUrl, getAuthToken);
      loadJobs();
    }
  }, [config, getAuthToken]);

  const filterJobs = useCallback(() => {
    let filtered = jobs;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(job =>
        (job.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (job.department || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (job.location || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(job => job.status === selectedFilter);
    }

    setFilteredJobs(filtered);
  }, [jobs, searchTerm, selectedFilter]);

  useEffect(() => {
    filterJobs();
  }, [filterJobs]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const jobsData = await apiService.getJobs();
      setJobs(jobsData);
    } catch (err) {
      setError('Failed to load jobs');
      console.error('Jobs loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterSelect = (filter) => {
    setSelectedFilter(filter);
    handleFilterClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'draft':
        return 'warning';
      case 'closed':
        return 'error';
      default:
        return 'default';
    }
  };

  const JobCard = ({ job }) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="h6" component="h2" gutterBottom>
            {job.title || 'Untitled Job'}
          </Typography>
          <Chip
            label={job.status || 'active'}
            color={getStatusColor(job.status)}
            size="small"
          />
        </Box>

        <Box display="flex" alignItems="center" mb={1}>
          <BusinessIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {job.department || 'No department specified'}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" mb={1}>
          <LocationIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {job.location || 'Remote'}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" mb={1}>
          <ScheduleIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Created: {formatDate(job.created_at || job.posted)}
          </Typography>
        </Box>

        {job.description && (
          <Typography variant="body2" color="text.secondary" paragraph>
            {job.description.length > 150
              ? `${job.description.substring(0, 150)}...`
              : job.description
            }
          </Typography>
        )}

        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {job.skills && job.skills.slice(0, 3).map((skill, index) => (
            <Chip
              key={index}
              label={skill}
              size="small"
              variant="outlined"
            />
          ))}
          {job.skills && job.skills.length > 3 && (
            <Chip
              label={`+${job.skills.length - 3} more`}
              size="small"
              variant="outlined"
              color="primary"
            />
          )}
        </Box>

        <Box display="flex" gap={1} mt="auto">
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/job/${job.jobId}`)}
          >
            View Details
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate(`/candidate-matching?jobId=${job.jobId}`)}
          >
            Find Candidates
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <WorkIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Jobs
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/create-job')}
          >
            Create Job
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage your job postings and find the best candidates.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filter Bar */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          fullWidth
          placeholder="Search jobs by title, department, or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <IconButton
          onClick={handleFilterClick}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <FilterIcon />
        </IconButton>
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
        >
          <MenuItem onClick={() => handleFilterSelect('all')}>
            All Jobs
          </MenuItem>
          <MenuItem onClick={() => handleFilterSelect('active')}>
            Active Jobs
          </MenuItem>
          <MenuItem onClick={() => handleFilterSelect('draft')}>
            Draft Jobs
          </MenuItem>
          <MenuItem onClick={() => handleFilterSelect('closed')}>
            Closed Jobs
          </MenuItem>
        </Menu>
      </Box>

      {/* Jobs Grid */}
      {filteredJobs.length > 0 ? (
        <Grid container spacing={3}>
          {filteredJobs.map((job, index) => (
            <Grid item xs={12} md={6} lg={4} key={job.jobId || index}>
              <JobCard job={job} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box textAlign="center" py={8}>
          <WorkIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm || selectedFilter !== 'all' ? 'No jobs match your criteria' : 'No jobs found'}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {searchTerm || selectedFilter !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Create your first job posting to get started with recruitment.'
            }
          </Typography>
          {!searchTerm && selectedFilter === 'all' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/create-job')}
            >
              Create Your First Job
            </Button>
          )}
        </Box>
      )}
    </Container>
  );
};

export default JobsPage;
